import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { map, OperatorFunction } from 'rxjs';

import { Survey } from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveySaveInputBuilder } from '../../../../services/save/survey-save-input.builder';
import { selectSurvey } from '../../survey-creation-core.selectors';
import {
  SurveySaveBuild,
  SurveyUpdateOverrides,
  ValidatedSurveyForSave,
} from '../survey-save.pipeline.types';

// Build stage: decides write, skip or reject; the input builder builds the request.
@Injectable({ providedIn: 'root' })
export class SurveySaveBuildStage {
  private readonly _store = inject(Store);
  private readonly _input = inject(SurveySaveInputBuilder);

  public build(
    overrides: SurveyUpdateOverrides,
  ): OperatorFunction<ValidatedSurveyForSave, SurveySaveBuild> {
    return (validated$) =>
      validated$.pipe(
        concatLatestFrom(() => this._store.select(selectSurvey)),
        map(([validated, survey]) =>
          this._buildValidated(validated, survey, overrides),
        ),
      );
  }

  private _buildValidated(
    validated: ValidatedSurveyForSave,
    survey: Nullable<Survey>,
    overrides: SurveyUpdateOverrides,
  ): SurveySaveBuild {
    const { silent } = validated;

    return this._input.request(validated, survey, overrides).match({
      ok: (request): SurveySaveBuild => ({
        kind: 'write',
        request,
        survey,
        silent,
      }),
      err: (error): SurveySaveBuild => ({ kind: 'reject', error }),
    });
  }
}
